import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { Button } from '@artosera/ui';
import { deconnexion } from '../lib/api.js';
import { estRoleConsole, useSession } from './useSession.js';
import styles from './RequireConsoleAuth.module.css';

/** Le seul écran qu'une session confinée à l'enrôlement 2FA peut ouvrir. */
export const CHEMIN_SECURITE = '/securite';

/**
 * Garde de routage (S2-g) : redirige vers /connexion tant que la session
 * n'est pas établie, et refuse l'accès à qui n'a pas un rôle `venio_*` — la
 * console est réservée à Venio (5.3, D14). Une session confinée à
 * l'enrôlement du second facteur ne voit que `/securite` (ARTO-58).
 */
export function RequireConsoleAuth({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation('console');
  const location = useLocation();
  const { moi, enChargement, erreurAuthentification, confine } = useSession();

  if (enChargement) {
    return (
      <div className={styles.etat} role="status">
        {t('session.verification')}
      </div>
    );
  }

  // Avant le test « pas de session » : un `/me` refusé pour confinement n'est
  // pas une déconnexion. La requête suit (`?demo`).
  if (confine && location.pathname !== CHEMIN_SECURITE) {
    return <Navigate to={{ pathname: CHEMIN_SECURITE, search: location.search }} replace />;
  }
  if (confine && !moi) return <>{children}</>;

  if (erreurAuthentification || !moi) {
    return (
      <Navigate
        to="/connexion"
        replace
        state={{ depuis: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!estRoleConsole(moi.compte.rolePlateforme)) {
    return (
      <div className={styles.etat}>
        <h1>{t('session.accesRefuseTitre')}</h1>
        <p>{t('session.accesRefuseTexte')}</p>
        <Button
          variant="secondary"
          onClick={() => void deconnexion().then(() => window.location.assign('/connexion'))}
        >
          {t('session.seDeconnecter')}
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}
